<template lang="html">
  <v-container fluid class="px-sm-10">
    <v-row>
      <v-col cols="auto" class="headline">Barview Access</v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card :loading="loadingBarviewRegistrations || state.saving">
          <v-card-title>
            Registration requests
            <v-spacer/>
            <v-btn @click="fetchBarviewRegistrations"><v-icon left>mdi-refresh</v-icon>Update</v-btn>
          </v-card-title>
          <v-card-text>
            <div class="mb-3">
              These are devices that have requested to get barview access. They are active for 30 minutes after the last request before they go away.
            </div>
            <v-data-table
              :headers="barviewRegistrationsTableHeaders"
              :items="barviewRegistrations"
              :items-per-page="-1"
              class="barviewregistrations-table"
              must-sort
              sort-by="requested_at"
              :sort-desc="true"
            >
              <template v-slot:item.device_name="{ item }">
                <span @dblclick.stop="$dialog({title: 'Installation Id', message: item.installation_id})">{{item.device_name}}</span>
              </template>
              <template v-slot:item.requested_at="{ item }">
                <span>{{moment(item.requested_at).fromNow()}}</span>
              </template>
              <template v-slot:item.installation_id="{ item }">
                <span v-if="item.granted" class="success--text"><v-icon small left color="success">mdi-check</v-icon>Has access</span>
                <span v-else><v-btn color="primary" @click="grantAccess(item)" :disabled="state.saving">Give Access</v-btn></span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <base-items store-module="barviewaccess" @items="items = $event" @state="state = $event">
          <v-card :loading="state.loading || state.deleting">
            <v-card-title>
              Registered Barviews
            </v-card-title>
            <v-card-text>
              <div class="mb-3">
                These are devices that have been given barview access.
              </div>
              <v-data-table
                :headers="tableHeaders"
                :items="items"
                :items-per-page="-1"
                class="barviewaccess-table"
                must-sort
                sort-by="created_at"
                :sort-desc="true"
                @click:row="editBarviewId = $event.id"
              >
                <template v-slot:item.point_of_sale_id="{ item }">
                  <span>{{!item.is_point_of_sale ? '' : item.point_of_sale_id}}</span>
                </template>
                <template v-slot:item.device_name="{ item }">
                  <span @click.stop @dblclick.stop="$dialog({title: 'Installation Id', message: item.id})">{{item.device_name}}</span>
                </template>
                <template v-slot:item.id="{ item }">
                  <span><v-btn text icon @click.stop="deleteItem(item)" :disabled="state.deleting"><v-icon>mdi-delete</v-icon></v-btn></span>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </base-items>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            Activate Barview
          </v-card-title>

          <v-card-text>
            <div class="mb-3">
              Activate your barview for this shop by scanning the QR code below
            </div>

            <img
              class="qr-code"
              :src="qrUrl"
              alt="QR Code">
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <item-view-dialog
      :value="!!editBarviewId"
      @input="editBarviewId = null"
      storeModule="barviewaccess"
      :itemId="editBarviewId"
      title="Barview"
      :maxWidth="400"
    >
      <div></div>
      <template #itemForm="{item, required}">
        <div>
          <p class="title">{{item.device_name}}</p>

          <div class="text--primary">
            <p>Requested by {{item.name}}</p>
            <p>Granted at {{item.created_at}} by {{item.created_by}}</p>
            <p class="caption">Id: {{item.id}}</p>
          </div>
        </div>

        <v-divider/>

        <v-checkbox
          label="Is point of sale"
          v-model="item.is_point_of_sale"
        ></v-checkbox>

        <v-text-field
          label="Point of sale Id"
          v-model="item.point_of_sale_id"
          autocomplete="off"
          :disabled="!item.is_point_of_sale"
          :rules="item.is_point_of_sale ? [required] : []"
        ></v-text-field>

        <v-text-field
          label="Control unit Id"
          v-model="item.control_unit_id"
          placeholder="RIHTT..."
          autocomplete="off"
          :disabled="!item.is_point_of_sale"
          :rules="item.is_point_of_sale ? [required] : []"
        ></v-text-field>

        <v-text-field
          label="Manufacturing number"
          v-model="item.manufacturer_number"
          autocomplete="off"
          :disabled="!item.is_point_of_sale"
          :rules="item.is_point_of_sale ? [required] : []"
        ></v-text-field>
      </template>
    </item-view-dialog>
  </v-container>
</template>

<script>
import {BaseItems, ItemViewDialog} from 'dync-baseitem';
import moment from 'moment';
import {getItemById, apiUrl} from '@dynapp/wc-api-generic';

export default {
  data () {
    return {
      moment,
      items: [],
      state: {},
      editBarviewId: null,
      barviewRegistrationsTableHeaders: [
        {
          text: 'Device name',
          value: 'device_name'
        },
        {
          text: 'Requested by',
          value: 'name'
        },
        {
          text: 'Requested at',
          value: 'requested_at'
        },
        {
          text: 'Grant',
          value: 'installation_id',
          sortable: false
        }
      ],
      tableHeaders: [
        {
          text: 'Device name',
          value: 'device_name'
        },
        {
          text: 'Requested by',
          value: 'name'
        },
        {
          text: 'Point of sale',
          value: 'point_of_sale_id'
        },
        {
          text: 'Granted by',
          value: 'created_by'
        },
        {
          text: 'Granted at',
          value: 'created_at'
        },
        {
          text: 'Remove',
          value: 'id',
          sortable: false
        }
      ],
      intervalId: ''
    };
  },
  computed: {
    qrUrl() {
      return apiUrl({dataObject: 'dync-appshop-web'}, '/barview-qr')
    },
    loadingBarviewRegistrations() {
      return this.$store.getters.isLoadingFor('barviewregistrations.*');
    },
    barviewRegistrations() {
      var registrations = this.$store.getters['barviewregistrations/items'];
      return Object.keys(registrations).map((id) => {
        var item = Object.assign({}, registrations[id]);
        item.granted = !!getItemById(this.items, id);
        return item;
      });
    }
  },
  methods: {
    fetchBarviewRegistrations() {
      this.$store.dispatch('barviewregistrations/fetch');
    },
    grantAccess (item) {
      var payload = {
        id: item.installation_id,
        name: item.name,
        device_name: item.device_name,
        status: 'GRANTED'
      };
      this.$store.dispatch('barviewaccess/createItem', payload).then(() => {
        this.$snackbar({message: 'Access granted!', theme: 'success'})
      }).catch((error) => {
        console.error(error);
        this.$snackbar({message: 'Could not grant access', theme: 'error'})
      });
    },
    deleteItem (item) {
      this.$confirm({
        title: `Are you sure you want to remove barview access for "${item.device_name}"?`,
        message: 'The device will loose barview access, and will have to re-register to get it back.'
      }).then(() => {
        this.$store.dispatch('barviewaccess/deleteItem', item.id).then(() => {
          this.$snackbar({message: 'Access removed', theme: 'success'})
        }).catch((error) => {
          console.error(error);
          this.$snackbar({message: 'Could not remove access', theme: 'error'})
        });
      });
    }
  },
  mounted() {
    this.fetchBarviewRegistrations();

    // Auto-refresh list of registrations
    clearInterval(this.intervalId);
    this.intervalId = setInterval(this.fetchBarviewRegistrations, 10000);
  },
  beforeDestroy() {
    clearInterval(this.intervalId);
  },
  components: {
    BaseItems,
    ItemViewDialog
  }
}
</script>

<style lang="scss" scoped>
.barviewregistrations-table:deep thead:not(.v-data-table-header-mobile),
.barviewregistrations-table:deep tr:not(.v-data-table__mobile-table-row) {
  td:last-of-type,
  th:last-of-type {
      width: 200px;
  }
}

.barviewaccess-table:deep tr {
  cursor: pointer;
}

.barviewaccess-table:deep thead:not(.v-data-table-header-mobile),
.barviewaccess-table:deep tr:not(.v-data-table__mobile-table-row) {
  td:last-of-type,
  th:last-of-type {
      width: 100px;
  }
}

.qr-code {
  max-width: 100%;
  object-fit: contain;
}
</style>
